import {
  OverridableQuery,
  UseQueryOptions,
  restrictApi,
  useQuery,
} from '@wyz/utils';
import type {
  IErrorApiResponse,
  IOptionItem,
  ISuccessApiResponse,
} from '@wyz/types';
import { useApi, useI18n } from '~/common/context';
import { ConnectQueryKeys } from '~/connect/api';
import { PriceFrameTypesLookups } from '~/common/helpers';

type IReferenceGroupResult = {
  publicKey: string;
  name: string;
  documentId: string;
};
type Options = {
  getReferenceGroups?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<IReferenceGroupResult>>,
      IErrorApiResponse
    >
  >;
  getPriceFrameTypesQuery?: OverridableQuery<
    UseQueryOptions<Array<IOptionItem<string>>, IErrorApiResponse>
  >;
};

export const useManageParameters = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { t } = useI18n();

  const getReferenceGroupsQuery = useQuery({
    queryKey: ConnectQueryKeys.parameters.referenceGroups(),
    queryFn: () => {
      return callCoreApi<ISuccessApiResponse<Array<IReferenceGroupResult>>>(
        '/offers/api/catalog/parameters/reference-groups',
      );
    },
    refetchOnWindowFocus: false,
    enabled: false,
    gcTime: Infinity,
    staleTime: Infinity,
    ...options.getReferenceGroups,
  });

  const getPriceFrameTypesQuery = useQuery<
    Array<IOptionItem<string>>,
    IErrorApiResponse
  >({
    queryKey: ConnectQueryKeys.parameters.priceFrameTypes(),
    queryFn: async () => {
      const data = await callCoreApi<
        ISuccessApiResponse<Array<{ value: string; codename: string }>>
      >('/tenants/api/enums/price-frame-types');
      return data.data.map((type) => ({
        value: type.value,
        label: t(PriceFrameTypesLookups[type.codename]),
      }));
    },
    refetchOnWindowFocus: false,
    enabled: options.getPriceFrameTypesQuery?.enabled ?? false,
    gcTime: Infinity,
    staleTime: Infinity,
  });

  return restrictApi(options, {
    getReferenceGroups: getReferenceGroupsQuery,
    getPriceFrameTypesQuery: getPriceFrameTypesQuery,
  });
};
